import React, { ReactNode } from "react"

import { cn, resolveButtonHref, transformButtonStyle } from "@/lib/utils"
import { Link, TButton, Typography } from "@/components/ui/atoms"
import { Pagination, type TPagination } from "@/components/ui/molecules"

type TCardListContainerProps = {
  title?: string
  titleCenter?: boolean
  text?: string
  className?: string
  children: ReactNode
  backgroundWhite?: boolean
  centered?: boolean
  twoCols?: boolean
  paginationList?: TPagination[]
  buttons?: TButton[]
  smallInfo?: string
  variant?: "article" | "component"
  isHomepage?: boolean
  isInArticle?: boolean
}

const CardListContainer = ({
  title,
  titleCenter = false,
  centered = false,
  text,
  className,
  twoCols = false,
  backgroundWhite = false,
  buttons,
  children,
  smallInfo,
  variant = "component",
  paginationList,
  isHomepage = false,
  isInArticle,
}: TCardListContainerProps) => {
  const titleModifier = titleCenter ? "text-center" : ""

  return (
    <div
      className={cn(
        isHomepage
          ? "bg-neutral-200"
          : backgroundWhite
            ? "bg-neutral-100"
            : "bg-neutral-200",
        className
      )}
    >
      <div
        className={cn(
          variant === "article" ? "" : isInArticle ? "" : "container"
        )}
      >
        {title ? (
          <header className="pb-10 lg:pb-12">
            <Typography as="h2" variant="headingL" className={titleModifier}>
              {title}
            </Typography>
          </header>
        ) : null}
        {text ? (
          <Typography
            className="mx-auto mb-11 max-w-[648px] text-center lg:max-w-[904px] xl:max-w-[764px] 2xl:max-w-[936px]"
            variant="paragraphL"
          >
            {text}
          </Typography>
        ) : null}
        <section
          className={cn(
            centered
              ? "flex flex-wrap justify-center"
              : twoCols
                ? "grid gap-6 lg:grid-cols-2"
                : "grid gap-9 gap-y-8 lg:grid-cols-6"
          )}
        >
          {React.Children.count(children) === 2 ? (
            <span className="hidden lg:col-span-1 lg:block"></span>
          ) : React.Children.count(children) === 1 ? (
            <span className="col-span-2 hidden lg:block"></span>
          ) : null}
          {children}
        </section>
        {smallInfo ? (
          <div className="heading-s pt-10 text-center font-500 lg:pt-12">
            {smallInfo}
          </div>
        ) : null}
        {buttons || paginationList ? (
          <footer
            className={cn(
              "flex items-center justify-center",
              smallInfo ? "pt-6" : "pt-8 lg:pt-12"
            )}
          >
            {buttons
              ? buttons.map((button) => {
                  return (
                    <Link
                      chapters={[title, button.ctaLabel]}
                      key={button.ctaLink}
                      variant={transformButtonStyle(button.style)}
                      iconLeft={button.iconLeft ?? undefined}
                      iconRight={button.iconRight ?? undefined}
                      href={resolveButtonHref(button)}
                    >
                      {button.ctaLabel}
                    </Link>
                  )
                })
              : null}
            {paginationList && !buttons ? (
              <Pagination className="mt-11" list={paginationList} />
            ) : null}
          </footer>
        ) : null}
      </div>
    </div>
  )
}

export { CardListContainer, type TCardListContainerProps }
